import { Injectable, signal } from '@angular/core';

export type Theme = 'light' | 'dark';

/**
 * Servicio centralizado para gestionar el tema de la app (light/dark).
 * - Persiste en localStorage.
 * - Usa data-theme en <html> para que los estilos respondan.
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_KEY = 'app-theme';
  private readonly ATTR = 'data-theme';

  // Signal para reactividad en componentes standalone
  // Inicializa con 'light' por defecto, se actualizará cuando el navegador esté listo
  currentTheme = signal<Theme>('light');

  constructor() {
    // Solo inicializar si estamos en el navegador
    if (this.isBrowser()) {
      const initialTheme = this.getInitial();
      this.currentTheme.set(initialTheme);
      this.apply(initialTheme);
    }
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined' && typeof document !== 'undefined';
  }

  private getInitial(): Theme {
    if (!this.isBrowser()) {
      return 'light'; // Default para SSR
    }

    try {
      const saved = localStorage.getItem(this.THEME_KEY) as Theme;
      if (saved === 'light' || saved === 'dark') {
        return saved;
      }

      // Verificar preferencia del sistema
      // if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      //   return 'light';
      // }
    } catch (error) {
      console.warn('Error al obtener tema inicial:', error);
    }

    return 'light'; // Default
  }

  set(theme: Theme) {
    this.currentTheme.set(theme);
    
    if (this.isBrowser()) {
      this.apply(theme);
      try {
        localStorage.setItem(this.THEME_KEY, theme);
      } catch (error) {
        console.warn('Error al guardar tema en localStorage:', error);
      }
    }
  }

  toggle() {
    this.set(this.currentTheme() === 'light' ? 'dark' : 'light');
  }

  isDark() {
    return this.currentTheme() === 'dark';
  }

  private apply(theme: Theme) {
    if (!this.isBrowser()) {
      return; // No aplicar si no estamos en el navegador
    }

    try {
      const html = document.documentElement;
      html.setAttribute(this.ATTR, theme);
      html.classList.toggle('dark-mode', theme === 'dark');
    } catch (error) {
      console.warn('Error al aplicar tema:', error);
    }
  }
}

